import './SendValentineDaySticker.sass';

import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRoute } from 'react-router5';

import { Button, Header, Placeholder, ScrollView } from '$uikit';
import { useTranslator, useUnitName, useUserInfo } from '$hooks';
import { Api } from '$api';
import { mainActions } from '$store/main';
import { toastActions } from '$store/toast';
import { Loader, UnitPhoto } from '$shared/components';
import { imActions } from '$store/im';
import { AuthPopoverSimple } from '$core/AuthPopover/AuthPopover';
import { statReachGoal } from '$utils';
import { balancePayActions } from '$store/balancePay';
import { BalancePayAction } from '$store/balancePay/interface';
import { put } from 'redux-saga/effects';
import { profileActions } from '$store/profile';
import { AuthPopoverState } from '$store/models';
import { authSelector } from '$store/auth';

export const SendValentineDaySticker: FC = () => {
  const { route } = useRoute();
  const userId = +route.params.userId;

  const t = useTranslator();
  const dispatch = useDispatch();
  const user = useUserInfo(userId);
  const { isLogged } = useSelector(authSelector);
  const userName = useUnitName(user, 'accusative', 'firstName');
  const [loadingStatus, setLoadingStatus] = useState('');

  const loadData = useCallback(() => {
    setLoadingStatus('loading');
    Api.get<any, any>(`/balance/subscription/author/${userId}`)
      .then((data) => {
        dispatch(mainActions.setUser(data.user));
        setLoadingStatus('');
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
        setLoadingStatus('failed');
      });
  }, []);

  useEffect(() => {
    if (!user) {
      loadData();
    }
  }, []);

  const handleSend = useCallback(() => {
    if (isLogged) {
      dispatch(
        balancePayActions.open({
          amount: 1,
          action: BalancePayAction.VALENTINE,
          objectId: userId,
        }),
      );
    } else {
      dispatch(profileActions.setAuthPopoverState(AuthPopoverState.REGISTER));
    }
  }, [userId]);

  function renderContent() {
    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (loadingStatus === 'loading' || !user) {
      return <Loader isWrapped />;
    }

    return (
      <div className="SendValentineDaySticker">
        <div className="SendValentineDaySticker__content">
          <div className="SendValentineDaySticker__photo">
            <div className="SendValentineDaySticker__photo__decorations top" />
            <div className="SendValentineDaySticker__photo__decorations bottom" />
            <UnitPhoto unit={user} size="extra-large" />
          </div>
          <div className="SendValentineDaySticker__title">
            {t('valentine_day_send_content_title', { name: user.firstName })}
          </div>
          <div className="SendValentineDaySticker__caption">
            {t('valentine_day_send_content_caption', { name: userName })}
          </div>
        </div>
        <div className="SendValentineDaySticker__footer">
          <Button fillColor="pink" onClick={handleSend}>
            {t('valentine_day_send_button')}
          </Button>
          <div className="SendValentineDaySticker__details">
            {t('valentine_day_send_details')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header showCloseButton>{t('valentine_day_send_title')}</Header>
      <ScrollView>{renderContent()}</ScrollView>
      <AuthPopoverSimple />
    </>
  );
};
